import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ScriptCoPreview from "./ScriptCoPreview";

const MIME_TYPES_BY_EXTENSION = [
  [".pdf", "application/pdf"],
  [".png", "image/png"],
  [".jpg", "image/jpeg"],
  [".jpeg", "image/jpeg"],
  [".gif", "image/gif"],
];

const inferMimeType = (mimeType, lowerName) => {
  if (mimeType && mimeType !== "application/octet-stream") return mimeType;
  const found = MIME_TYPES_BY_EXTENSION.find(([ext]) =>
    lowerName.endsWith(ext),
  );
  return found ? found[1] : "application/pdf";
};

const isRemoteOrBlobUrl = (str) =>
  str.startsWith("blob:") ||
  str.startsWith("http://") ||
  str.startsWith("https://");

const resolvePreviewSource = (fileData) => {
  const rawData = fileData.base64 || fileData.content || fileData.data;
  if (rawData) {
    return {
      rawData,
      mimeType: fileData.mimeType || fileData.type,
      fileName: fileData.fileName || fileData.name,
    };
  }
  return null;
};

const pdfTextToBlobUrl = (text) => {
  const byteArray = new Uint8Array(text.length);
  for (let i = 0; i < text.length; i++) {
    byteArray[i] = text.codePointAt(i) & 0xff;
  }
  const blob = new Blob([byteArray], { type: "application/pdf" });
  return URL.createObjectURL(blob);
};

const base64ToBlobUrl = (base64, mimeType) => {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.codePointAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: mimeType });
  return URL.createObjectURL(blob);
};

const ScriptCoPatientDocumentUpload = ({
  data,
  onChange,
  onValidationChange,
  showErrors,
  hideHeader = false,
}) => {
  const fileInputRef = useRef(null);
  const [error, setError] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  const fileData = data || null; // { file, fileName, fileType, base64, size }

  React.useEffect(() => {
    const isValid = !!(
      fileData &&
      (fileData.base64 ||
        fileData.file ||
        fileData.fileName ||
        fileData.patientProofPath)
    );
    if (onValidationChange) {
      onValidationChange(isValid);
    }
  }, [fileData, onValidationChange]);

  const handleFileSelect = (file) => {
    if (!file) return;

    setError("");
    const allowedExtensions = ["pdf", "png"];
    const extension = file.name.split(".").pop().toLowerCase();

    if (!allowedExtensions.includes(extension)) {
      setError("Invalid file format. Only PNG or PDF files are allowed.");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError("File size exceeds 10MB limit.");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const base64Data = reader.result.split(",")[1];
      const newFileData = {
        file,
        fileName: file.name,
        fileType: extension,
        size: file.size,
        base64: base64Data,
        mimeType:
          file.type || (extension === "pdf" ? "application/pdf" : "image/png"),
      };
      if (onChange) {
        onChange(newFileData);
      }
    };
    reader.onerror = () => {
      setError("Failed to read file.");
    };
    reader.readAsDataURL(file);
  };

  const handleInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
    e.target.value = null;
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleRemove = () => {
    setError("");
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    if (onChange) {
      onChange(null);
    }
  };

  const createBlobUrl = (base64Str, mimeType, fileName) => {
    if (!base64Str) return null;
    try {
      let cleanStr = String(base64Str).trim();

      if (isRemoteOrBlobUrl(cleanStr)) {
        return cleanStr;
      }

      const lowerName = (fileName || "").toLowerCase();
      let effectiveMimeType = inferMimeType(mimeType, lowerName);

      if (cleanStr.startsWith("data:")) {
        const parts = cleanStr.split(",");
        const mimeMatch = /data:(.*?);/.exec(parts[0]);
        if (mimeMatch) effectiveMimeType = mimeMatch[1];
        cleanStr = parts[1] || "";
      }

      if (cleanStr.startsWith("%PDF")) {
        return pdfTextToBlobUrl(cleanStr);
      }

      return base64ToBlobUrl(cleanStr, effectiveMimeType);
    } catch (e) {
      console.error("Error creating blob URL for preview:", e);
      if (typeof base64Str === "string" && !base64Str.startsWith("data:")) {
        const lowerName = (fileName || "").toLowerCase();
        const type = lowerName.endsWith(".pdf")
          ? "application/pdf"
          : "image/png";
        return `data:${type};base64,${base64Str}`;
      }
      return base64Str;
    }
  };

  const handleOpenPreview = () => {
    if (!fileData) return;
    const previewSource = resolvePreviewSource(fileData);
    if (fileData.file) {
      const url = URL.createObjectURL(fileData.file);
      setPreviewUrl(url);
    } else if (previewSource) {
      const url = createBlobUrl(
        previewSource.rawData,
        previewSource.mimeType,
        previewSource.fileName,
      );
      setPreviewUrl(url);
    } else if (fileData.patientProofPath || fileData.url || fileData.fileUrl) {
      setPreviewUrl(
        fileData.patientProofPath || fileData.url || fileData.fileUrl,
      );
    }
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "";
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const isMissing = !fileData && showErrors;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        width: "100%",
      }}
    >
      {!hideHeader && (
        <Typography
          sx={{ fontSize: "13px", color: "#66638C", fontWeight: 500 }}
        >
          Patient Document (Patient Consent or DL Docs)<span style={{ color: "#E53935" }}>*</span>
        </Typography>
      )}

      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleInputChange}
        accept=".pdf,.png"
      />

      {!fileData ? (
        <Box
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          sx={{
            border:
              isMissing || error
                ? "1.5px dashed #d32f2f"
                : "1.5px dashed #403C6F",
            borderRadius: "8px",
            backgroundColor: isMissing || error ? "#FFF5F5" : "#F9FAFB",
            py: 1.75,
            px: 2.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            cursor: "pointer",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              backgroundColor: "rgba(64, 60, 111, 0.04)",
              borderColor: "#403C6F",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              minWidth: 0,
            }}
          >
            <CloudUploadOutlinedIcon
              sx={{ fontSize: 28, color: "#403C6F", flexShrink: 0 }}
            />
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: 500,
                  color: "#0e1324",
                }}
              >
                Drag and drop your file here, or{" "}
              </Typography>
              <Typography sx={{ fontSize: "11px", color: "#9F9DB6" }}>
                Supports single file (PNG or PDF format, max 10MB)
              </Typography>
            </Box>
          </Box>

          <Button
            variant="contained"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              fileInputRef.current?.click();
            }}
            sx={{
              backgroundColor: "#403C6F",
              color: "#FFF",
              borderRadius: "50px",
              textTransform: "none",
              px: 2.5,
              py: 0.6,
              fontWeight: 500,
              fontSize: "13px",
              boxShadow: "none",
              whiteSpace: "nowrap",
              flexShrink: 0,
              "&:hover": {
                backgroundColor: "#312e5e",
                boxShadow: "none",
              },
            }}
          >
            Select Document
          </Button>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            border: "1px solid #E8E8E9",
            borderRadius: "8px",
            p: 2,
            backgroundColor: "#F9FAFB",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <InsertDriveFileOutlinedIcon
              sx={{ fontSize: 32, color: "#403C6F" }}
            />
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#0e1324",
                  wordBreak: "break-all",
                }}
              >
                {fileData.fileName}
              </Typography>
              <Typography sx={{ fontSize: "12px", color: "#66638C" }}>
                {formatFileSize(fileData.size)}{" "}
                {fileData.fileType
                  ? `• ${fileData.fileType.toUpperCase()}`
                  : ""}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {!!(fileData.file || fileData.base64) && (
              <IconButton
                size="small"
                onClick={handleOpenPreview}
                title="Preview Document"
                sx={{
                  color: "#403C6F",
                  "&:hover": { backgroundColor: "rgba(64, 60, 111, 0.08)" },
                }}
              >
                <VisibilityOutlinedIcon sx={{ fontSize: 20 }} />
              </IconButton>
            )}
            <IconButton
              size="small"
              onClick={handleRemove}
              title="Remove File"
              sx={{
                color: "#d32f2f",
                "&:hover": { backgroundColor: "rgba(211, 47, 47, 0.08)" },
              }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
        </Box>
      )}

      {(error || isMissing) && (
        <Typography
          sx={{ color: "#d32f2f", fontSize: "12px", fontWeight: 500, mt: -0.5 }}
        >
          {error || "Patient Document (Patient Consent or DL Docs) is required (PNG or PDF format)."}
        </Typography>
      )}

      {previewOpen && (
        <Dialog
          open={previewOpen}
          onClose={handleClosePreview}
          maxWidth="md"
          fullWidth
          slotProps={{
            paper: {
              sx: {
                borderRadius: "16px",
                p: 2,
                maxHeight: "90vh",
                display: "flex",
                flexDirection: "column",
              },
            },
          }}
        >
          <DialogTitle
            sx={{
              p: 1,
              fontWeight: 700,
              fontSize: "18px",
              color: "#10100F",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>Patient Document (Patient Consent or DL Docs) Preview</span>
            <IconButton
              onClick={handleClosePreview}
              size="small"
              sx={{ color: "#9F9DB6", "&:hover": { color: "#E53935" } }}
            >
              <CloseIcon sx={{ fontSize: "20px" }} />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 1, mt: 1 }}>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 600,
                color: "#403C6F",
                mb: 1.5,
              }}
            >
              File:{" "}
              <span style={{ fontWeight: 400, color: "#10100F" }}>
                {fileData?.fileName}
              </span>
            </Typography>
            <ScriptCoPreview
              fileUrl={previewUrl}
              fileName={fileData?.fileName}
            />
          </DialogContent>
          <DialogActions sx={{ p: 1 }}>
            <Button
              onClick={handleClosePreview}
              variant="contained"
              sx={{
                borderRadius: "50px",
                textTransform: "none",
                fontWeight: 500,
                fontSize: "14px",
                backgroundColor: "#403C6F",
                color: "#FFF",
                px: 4,
                py: 1,
                boxShadow: "none",
                "&:hover": { backgroundColor: "#312e5e", boxShadow: "none" },
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

ScriptCoPatientDocumentUpload.propTypes = {
  data: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onValidationChange: PropTypes.func,
  showErrors: PropTypes.bool,
};

export default ScriptCoPatientDocumentUpload;
